<template>
  <div class="home-page">
    <!-- Hero Carousel Section -->
    <HeroCarousel :slides="heroSlides" />
    <section class="brand">
      <div class="brand-left">
        <div class="section-logo-text">Brand Introduction</div>
        <h2 v-if="$t('common.locale') !== 'en'" class="section-title">{{ $t('home.brand_title') }}</h2>
        <div class="section-content">
          {{ locale === 'zh' ? '三福，' : 'Sanfu, ' }}{{ $t('about.hero.line1') }}
          <br />
          {{ $t('about.hero.line2') }}
          <br />
          {{ $t('about.hero.line3') }}
          <br />
        </div>
        <div class="section-content section-content-mini">
          {{ locale === 'zh' ? '三福，' : 'Sanfu, ' }}{{ $t('about.hero.line1') }}
          <br />
          {{ $t('about.hero.line2') }}，{{ $t('about.hero.line3') }}
          <br />
        </div>
        <NuxtLinkLocale to="/about" class="btn btn-primary">
          {{ $t('home.brand_more') }}
          <!-- <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
        </svg> -->
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
          </svg>
        </NuxtLinkLocale>
      </div>
      <img class="brand-right" src="https://www-img.sanfu.com/production/index_brand.png" alt="SANFU" />
    </section>
    <section class="video-section" v-if="video && video.video_src">
      <div class="video-wrap">
        <VideoPlayer class="video-player" :src="(locale === 'id' ? video.video_src_id : locale === 'en' ? video.video_src_en : '') || video.video_src" :placeholderImgSrc="video.placeholderImgSrc" />
        <div class="video-content">
          <span v-if="video.title_zh" class="video-title">
            {{ locale === 'id' ? video.title_id : locale === 'en' ? video.title_en : video.title_zh }}
          </span>
          <span v-if="video.subTitle_zh" class="video-description">{{ locale === 'id' ? video.subTitle_id : locale === 'en' ? video.subTitle_en : video.subTitle_zh }}</span>
        </div>
      </div>
    </section>
    <!-- goods Section -->
    <section v-if="goodsImg && goodsImg.length" class="goods-section">
      <div class="goods-section-header">{{ locale === 'zh' ? '精选好物推荐' : locale === 'id' ? 'Pilihan Barang Terbaik' : 'Featured Goods' }}</div>
      <div class="goods-carousel-wrapper" @mouseenter="pauseCarousel" @mouseleave="resumeCarousel">
        <!-- 左箭头 -->
        <button class="carousel-arrow carousel-arrow-left" @click="scrollCarousel('left')" aria-label="向左滚动">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>

        <!-- 右箭头 -->
        <button class="carousel-arrow carousel-arrow-right" @click="scrollCarousel('right')" aria-label="向右滚动">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>

        <div class="goods-carousel">
          <div ref="carouselTrack" class="goods-carousel-track" :class="{ paused: isPaused }">
            <div v-for="(img, index) in goodsImg" :key="index" class="goods-item">
              <el-image lazy :src="img" :alt="`精选商品 ${index + 1}`" class="goods-image" :preview-src-list="goodsImg" preview-teleported :initial-index="index" hide-on-click-modal fit="cover" />
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
  import { ref, onMounted, onUnmounted } from 'vue'
  // 获取国际化函数
  const { t, locale } = useI18n()

  // 设置页面元数据
  useHead({
    title: `${t('navigation.logo')} - ${t('footer.brandDescription')}`,
    meta: [{ name: 'description', content: t('home.meta.description') }]
  })

  // 轮播图数据（初始为空，将通过API获取）
  const heroSlides = ref([])

  // 使用 useAsyncData 获取banner数据，支持SSR，避免水合错误和重复请求
  const { data: bannerRes, error: bannerError } = await useAsyncData('heroBanner', () => $fetch('/api/banner/getList'))
  const bannerData = bannerRes.value?.data
  // 如果获取数据成功，更新轮播图数据
  if (bannerData && Array.isArray(bannerData) && bannerData.length) {
    heroSlides.value = bannerData.map(slide => {
      if (slide.img) {
        slide.bg = `url(${slide.img}) center / cover no-repeat`
      }
      return slide
    })
  } else {
    // 使用默认数据作为后备
    heroSlides.value = [
      {
        h: 72,
        bg: 'linear-gradient(135deg, var(--brand-orange) 0%, var(--brand-blue) 50%)'
      }
    ]
  }

  // 轮播图数据（初始为空，将通过API获取）
  const goodsImg = ref([])

  // 使用 useAsyncData 获取banner数据，支持SSR，避免水合错误
  const { data: goodsRes } = await useAsyncData('goodsBanner', () =>
    $fetch('/api/banner/getList', {
      method: 'GET',
      params: {
        type: 2,
        pageSize: 10
      }
    })
  )
  const goods = goodsRes.value?.data
  // 如果获取数据成功，更新轮播图数据
  if (goods && Array.isArray(goods) && goods.length) {
    goodsImg.value = goods.map(slide => slide.img)
  }

  const video = ref([])
  // 使用 useAsyncData 获取video数据，支持SSR，避免水合错误
  const { data: videoApiRes } = await useAsyncData('homeVideo', () =>
    $fetch('/api/common_cfg/get', {
      method: 'GET',
      params: {
        cfg_type: 'home_video'
      }
    })
  )
  const video_res = videoApiRes.value?.data
  // 如果获取数据成功，更新轮播图数据
  if (video_res) {
    video.value = video_res.cfg_content
    // console.log(video.value)
  }

  // 商品轮播相关状态
  const carouselTrack = ref(null)
  const isPaused = ref(false)
  const currentPosition = ref(0)
  const autoScrollInterval = ref(null)
  const resetTimeout = ref(null) // 添加重置定时器引用
  const isInViewport = ref(false) // 添加是否在视口内的状态

  // 暂停自动滚动
  const pauseCarousel = () => {
    isPaused.value = true
  }

  // 恢复自动滚动
  const resumeCarousel = () => {
    isPaused.value = false
  }

  // 自动滚动功能
  const autoScroll = () => {
    // 只有在视口内且未暂停时才滚动
    if (!isInViewport.value || isPaused.value || !carouselTrack.value) return

    // 获取容器宽度
    const container = carouselTrack.value.closest('.goods-carousel')
    const containerWidth = container ? container.offsetWidth : typeof window !== 'undefined' ? window.innerWidth : 800

    // 计算最大滚动距离
    const trackWidth = carouselTrack.value.scrollWidth || goodsImg.value.length * 200
    const maxScroll = -(trackWidth - containerWidth)

    // 如果已经滚动到最右边，重置到最左边
    if (currentPosition.value <= maxScroll) {
      currentPosition.value = 20
      // 重置时移除过渡效果，避免动画
      carouselTrack.value.classList.add('no-transition')
      carouselTrack.value.style.setProperty('--scroll-position', `${currentPosition.value}px`)

      // 暂停滚动2秒
      isPaused.value = true

      // 在下一帧恢复过渡效果
      requestAnimationFrame(() => {
        if (carouselTrack.value) {
          carouselTrack.value.classList.remove('no-transition')
        }
      })

      resetTimeout.value = setTimeout(() => {
        isPaused.value = false
      }, 2000)
    } else {
      // 否则继续向左滚动
      currentPosition.value -= 1 // 每次滚动1px
      carouselTrack.value.style.setProperty('--scroll-position', `${currentPosition.value}px`)
    }
  }

  // 左右滚动功能
  const scrollCarousel = direction => {
    if (!carouselTrack.value) return

    // 暂停自动滚动
    pauseCarousel()

    // 获取容器宽度
    const container = carouselTrack.value.closest('.goods-carousel')
    const containerWidth = container ? container.offsetWidth : typeof window !== 'undefined' ? window.innerWidth : 800

    // 根据方向滚动
    const scrollAmount = containerWidth * 0.8 // 每次滚动80%的容器宽度
    if (direction === 'left') {
      currentPosition.value = Math.min(0, currentPosition.value + scrollAmount)
    } else {
      // 计算最大滚动距离
      const trackWidth = carouselTrack.value.scrollWidth || goodsImg.value.length * 200 // 估算宽度
      const maxScroll = -(trackWidth - containerWidth)
      currentPosition.value = Math.max(maxScroll, currentPosition.value - scrollAmount)
    }

    // 应用变换
    carouselTrack.value.style.setProperty('--scroll-position', `${currentPosition.value}px`)

    // 3秒后恢复自动滚动
    setTimeout(() => {
      isPaused.value = false
    }, 3000)
  }

  onMounted(() => {
    // 启动自动滚动
    autoScrollInterval.value = setInterval(autoScroll, 30)

    // 创建 Intersection Observer 来监听商品区域是否在视口中
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    }

    const goodsSectionObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // 当商品区域进入视口时，开始自动滚动
          isInViewport.value = true
        } else {
          // 当商品区域离开视口时，停止自动滚动
          isInViewport.value = false
        }
      })
    }, observerOptions)

    // 观察商品区域
    const goodsSection = document.querySelector('.goods-section')
    if (goodsSection) {
      goodsSectionObserver.observe(goodsSection)
    }

    // observer 阈值 (原有的动画观察器)
    const animateObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in')
        }
      })
    }, observerOptions)

    // 观察需要动画的元素
    const animateElements = document.querySelectorAll('.section-logo-text, .section-title, .section-content, .btn')
    animateElements.forEach(el => animateObserver.observe(el))
  })

  onUnmounted(() => {
    // 清理自动滚动定时器
    if (autoScrollInterval.value) {
      clearInterval(autoScrollInterval.value)
    }
    // 清理重置定时器
    if (resetTimeout.value) {
      clearTimeout(resetTimeout.value)
    }
  })
</script>

<style scoped lang="less">
  /* Hero Carousel Section */
  .home-page {
    margin-top: -70px;
  }

  .brand {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-24) var(--space-24);
    gap: 5vw;
    margin: var(--space-24) auto;
  }

  .brand .brand-left {
    flex: 1;
    max-width: 500px;
  }

  .section-logo-text {
    white-space: nowrap;
    font-size: 70px;
    letter-spacing: -3px;
    color: #323232;
    font-family: 'impact';
    line-height: 1;
    margin-bottom: var(--space-4);
    user-select: none;
    opacity: 0;
    transform: translateY(30px);
    transition: all var(--transition-base);
  }

  .section-title {
    position: relative;
    user-select: none;
    font-size: 46px;
    font-weight: 700;
    margin-bottom: var(--space-8);
    color: #000;
    opacity: 0;
    transform: translateY(30px);
    transition: all var(--transition-base);
    &::after {
      content: '';
      width: 50px;
      position: absolute;
      bottom: -8px;
      left: 0;
      right: 0;
      height: 3px;
      background-color: #000;
    }
  }

  .section-content {
    white-space: nowrap;
    margin-top: var(--space-4);
    color: #808080;
    font-size: 18px;
    line-height: 2;
    max-width: 600px;
    word-break: break-word;
    opacity: 0;
    transform: translateY(30px);
    transition: all var(--transition-base);
  }
  .section-content-mini {
    display: none;
  }

  /* 添加按钮样式 */
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-2) var(--space-8);
    padding-right: var(--space-6);
    border-radius: var(--radius);
    font-weight: 500;
    text-align: center;
    cursor: pointer;
    transition: all var(--transition-base);
    text-decoration: none;
    border: none;
    margin-top: var(--space-6);
    opacity: 0;
    transform: translateY(30px);
    transition: all var(--transition-base);
    svg {
      width: 24px;
      height: 24px;
    }
  }

  .brand .btn-primary {
    background: #fff;
    color: #242424;
    border: 1px solid #606266;
    border-radius: 4px;
  }

  .brand .btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow);
    color: #fff;
    background: #aaa;
    border-color: #aaa;
  }

  .brand .brand-right {
    width: 28%;
    min-width: 300px;
    border-radius: 2px;
    display: block;
  }

  /* goods section styles */
  .goods-section {
    width: 70%;
    margin: 0 auto;
    padding: var(--space-12) var(--space-6);
    overflow: hidden;
    min-width: 800px;
  }

  .goods-section-header {
    text-align: left;
    margin-bottom: var(--space-8);
    font-size: 32px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #5f5f5f;
    letter-spacing: 0.3px;
  }

  .goods-carousel-wrapper {
    width: 100%;
    overflow: hidden;
    position: relative;
  }

  .carousel-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(255, 255, 255, 0.8);
    border: none;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 100; /* 提高z-index确保按钮可见 */
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    color: #333;
    /* 确保按钮可以接收点击事件 */
    pointer-events: auto;
  }

  .carousel-arrow:hover {
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .carousel-arrow-left {
    transform: translateY(-50%);
    left: 10px;
  }

  .carousel-arrow-right {
    right: 10px;
  }

  .goods-carousel {
    display: flex;
    gap: var(--space-4);
    /* 确保不会遮挡箭头按钮 */
    z-index: 1;
  }

  .goods-carousel-track {
    display: flex;
    gap: var(--space-4);
    width: max-content;
    /* 使用 CSS 变量控制滚动位置 */
    transform: translateX(var(--scroll-position, 0));
    /* 添加过渡效果 */
    transition: transform 0.5s ease;
  }

  .goods-carousel-track.no-transition {
    transition: none;
  }

  .goods-item {
    flex: 0 0 calc((100vw - 8 * var(--space-4) - 100px) / 6); /* 进一步减小图片尺寸 */
    min-width: 150px;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: var(--shadow);
  }

  .goods-image {
    width: 100%;
    height: 100%;
    min-height: 100px;
    object-fit: cover;
    display: block;
    aspect-ratio: 2/3; /* 纵向图片比例 */
  }

  /* Video Section Styles */
  .video-section {
    position: relative;
    width: 100%;
    overflow: hidden;
    background-image: url(https://www-img.sanfu.com/production/index_vbg.png);
    background-size: cover;
    background-repeat: no-repeat;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    border-radius: 4px;
  }

  .video-wrap {
    width: 50%;
    display: flex;
    flex-direction: column;
    margin: 5%;
    overflow: hidden;
    border-radius: 2px;
  }

  .video-content {
    background: #000000f0;
    color: #fff;
    display: flex;
    flex-direction: column;
    line-height: 1;
    opacity: 0.8;
    padding: var(--space-4) var(--space-6);
    .video-title {
      font-size: 28px;
      font-weight: 700;
      margin-bottom: var(--space-4);
    }
    .video-description {
      font-size: 18px;
    }
  }

  /* 添加动画类 */
  .fade-in {
    opacity: 1;
    transform: translateY(0);
  }

  /* Responsive Design */
  @media (max-width: 895px) {
    .section-logo-text {
      font-size: 56px;
    }
    .goods-section {
      width: 90%;
      min-width: unset;
    }
  }
  @media (max-width: 768px) {
    .brand {
      flex-direction: column;
      text-align: center;
      gap: var(--space-8);
      margin: var(--space-6) auto;
    }

    .brand-left {
      max-width: 100%;
      margin-bottom: var(--space-6);
    }

    .section-logo-text {
      margin-bottom: var(--space-2);
      white-space: normal;
    }
    .section-title {
      margin-bottom: var(--space-4);
    }
    .section-content {
      margin-left: auto;
      margin-right: auto;
      max-width: 600px;
    }

    .section-logo-text,
    .brand .section-title,
    .section-content,
    .btn {
      opacity: 1;
      transform: translateY(0);
    }

    .section-title::after {
      content: unset;
    }

    .video-section {
      justify-content: center;
    }
    .video-wrap {
      width: 85%;
      margin: 10% 5%;
    }
    .video-content {
      .video-title {
        font-size: 24px;
      }
      .video-description {
        font-size: 16px;
      }
    }
    /* goods section tablet styles */

    .goods-section {
      width: unset;
    }
    .goods-section .section-title {
      font-size: 24px;
    }
    .goods-item {
      flex: 0 0 calc((100vw - 6 * var(--space-4) - 80px) / 3);
      min-width: calc((100vw - 6 * var(--space-4) - 80px) / 3);
    }
  }

  @media (max-width: 550px) {
    .brand {
      padding: var(--space-12) var(--space-6);
      align-items: flex-start;
    }

    .brand-left {
      text-align: left;
      margin-bottom: var(--space-2);
    }

    .section-logo-text {
      font-size: 38px;
      text-align: left;
      width: 100%;
      letter-spacing: 0.1px;
    }

    .brand .section-title {
      text-align: left;
      width: 100%;
      font-size: 28px;
    }

    .section-content {
      font-size: 13px;
      line-height: 1.6;
      text-align: left;
      display: none;
    }
    .section-content-mini {
      display: block;
      white-space: normal;
    }
    .btn {
      font-size: 14px;
      margin-top: var(--space-4);
      padding: 4px var(--space-2);
      padding-right: 0;
      svg {
        width: 18px;
        height: 18px;
      }
    }
    .brand .btn-primary {
      border-color: #999;
    }
    .section-title::after {
      content: '';
      width: 10%;
    }

    .brand .brand-right {
      width: 100%;
    }

    .video-wrap {
      margin: 10% 5%;
      width: 90%;
    }
    .video-content {
      padding: var(--space-2) var(--space-3);
      .video-title {
        font-size: 16px;
      }
      .video-description {
        font-size: 11px;
      }
    }

    /* goods section mobile styles */
    .goods-carousel-wrapper {
      padding: 0 40px; /* 为箭头留出空间 */
    }

    .carousel-arrow {
      width: 30px;
      height: 30px;
    }

    .carousel-arrow-left {
      left: 5px;
    }

    .carousel-arrow-right {
      right: 5px;
    }
    .goods-section .section-title {
      font-size: 22px;
    }
    .goods-item {
      flex: 0 0 calc((100vw - 8 * var(--space-4) - 80px) / 2.5);
      min-width: 120px;
    }
  }
</style>
